import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {catchError, map, Observable, throwError} from 'rxjs';
import { tap } from 'rxjs/operators';
import { api } from '../common';

export interface IUserProfile {
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  cognitoIds: string[];
  balance: number;
  streakDays?: number;
}

export interface IUserCognito {
  sub: string;
  email_verified: boolean;
  iss: string;
  aud: string;
  event_id: string;
  token_use: string;
  auth_time: number;
  name?: string;
  exp: number;
  iat: number;
  email: string;
}

export interface UpdateProfilePayload {
  username?: string;
  avatar?: string;
  balance?: number; //TODO: это убрать и обновлять только на беке после оплаты
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private baseUrl = `${api}/users`;
  private userData: any = null;

  // Определяем сигналы для хранения состояния
  userCognito = signal<IUserCognito | null>(null);
  userProfile = signal<IUserProfile | null>(null);

  // Пример computed сигнала, производного от существующих сигналов
  isAuthenticated = computed(() => !!this.userCognito());

  // Метод загрузки данных профиля
  loadUserProfile(): Observable<{ user: IUserProfile; isNewUser: boolean }> {
    return this.http.get<{ user: IUserProfile, isNewUser: boolean }>(`${this.baseUrl}/profile`).pipe(
      tap(response => {
        this.userProfile.set(response.user);
      })
    );
  }

  // Метод обновления Cognito-данных после авторизации
  setUserCognito(cognitoData: IUserCognito) {
    this.userCognito.set(cognitoData);
  }

  // Обновление данных профиля
  updateProfile(payload: UpdateProfilePayload): Observable<any> {
    return this.http.patch<IUserProfile>(`${this.baseUrl}/profile`, payload).pipe(
      tap((updatedProfile) => {
        this.userProfile.set(updatedProfile);
      })
    );
  }

  userRequest(subject: string, description: string): Observable<any> {
    const email = this.userProfile()?.email;

    return this.http.post(`${this.baseUrl}/request`, {
      subject,
      description,
      email
    }).pipe(
      catchError((error) => {
        console.error('Caught in service:', error);
        return throwError(() => error);
      })
    );
  }

  // Проверка доступности имени пользователя
  checkUsername(username: string): Observable<boolean> {
    return this.http.post<{ available: boolean }>(`${this.baseUrl}/check-username`, {username})
      .pipe(
        tap(response => console.log('Username available:', response.available)),
        map(response => response.available)
      );
  }


  usePointsForGame(points: number): Observable<any> {
    return this.http.post<{ message: string; remainingPoints: number }>(
      `${this.baseUrl}/points/use`,
      { points }
    ).pipe(
      map((response) => {
        if (response?.remainingPoints !== undefined) {
          const current = this.userProfile();
          if (current) {
            this.userProfile.set({
              ...current,
              balance: response.remainingPoints
            });
          }
        }
        return response;
      }),
      catchError((err) => {
        console.error('Error using points:', err);
        return throwError(() => err);
      })
    );
  }

  canAffordPoints(points: number): Observable<{ canAfford: boolean }> {
    return this.http.get<{ canAfford: boolean }>(`${this.baseUrl}/check-balance`, {
      params: { points: points.toString() }
    });
  }

  addPoints(points: number): Observable<any> {
    return this.http.post<{ message: string; newBalance: number }>(
      `${this.baseUrl}/points/add`,
      { points }
    ).pipe(
      map((response) => {
        if (response?.newBalance !== undefined) {
          const current = this.userProfile();
          if (current) {
            this.userProfile.set({
              ...current,
              balance: response.newBalance
            });
          }
        }
        return response;
      }),
      catchError((err) => {
        console.error('Error adding points:', err);
        return throwError(() => err);
      })
    );
  }
}
